import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import VoiceInput from "@/components/VoiceInput";

export default function NewIdea() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [interim, setInterim] = useState("");
  const [voiceStyle, setVoiceStyle] = useState("blunt");
  const [voiceStyles, setVoiceStyles] = useState([]);
  const fileRef = useRef(null);
  const planFileRef = useRef(null);
  const [planFile, setPlanFile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/voice-styles");
        setVoiceStyles(r.data.styles || []);
        setVoiceStyle(r.data.default || "blunt");
      } catch {
        /* keep default */
      }
    })();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError("Both fields are required.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const r = await api.post("/ideas", {
        title: title.trim(),
        description: description.trim(),
        voice_style: voiceStyle,
      });
      const newIdeaId = r.data.idea.id;
      // Stage 4a: if the founder attached a plan PDF, upload it now (post
      // idea-creation, because /plan needs an idea_id). Non-blocking on
      // extraction — backend fires that in the background. If the upload
      // itself fails (413, corrupted PDF, etc.) we surface the error and
      // hold the founder on this page so they can retry or skip.
      if (planFile) {
        const form = new FormData();
        form.append("file", planFile);
        const planResp = await fetch(`${API}/ideas/${newIdeaId}/plan`, {
          method: "POST",
          credentials: "include",
          body: form,
        });
        if (!planResp.ok) {
          const detail = (await planResp.json().catch(() => ({}))).detail
            || "Plan upload failed.";
          setError(`Plan upload: ${detail}`);
          setLoading(false);
          return;
        }
      }
      nav(`/ideas/${newIdeaId}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start Ring 1.");
      setLoading(false);
    }
  }

  async function onFileChosen(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch(`${API}/extract-text`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || "Could not read that file.");
      }
      const data = await resp.json();
      // Append extracted text to whatever's already there (or replace if empty)
      setDescription((prev) =>
        prev.trim() ? `${prev}\n\n${data.text}` : data.text
      );
      // If title is empty, derive from filename
      if (!title.trim()) {
        const base = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").slice(0, 140);
        setTitle(base);
      }
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function onVoiceTranscript(chunk) {
    setDescription((prev) => (prev.trim() ? `${prev} ${chunk}` : chunk));
    setInterim("");
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-8 py-14">
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-10"
          onClick={() => nav("/dashboard")}
          data-testid="back-to-dashboard"
        >
          ← Dashboard
        </button>

        <div className="mono-caps mb-3">Intake</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">
          Describe the idea.
        </h1>
        <p className="text-slate-600 max-w-2xl leading-relaxed mb-12">
          The interrogator opens Ring 1 the moment you submit. Be specific.
          Vagueness in intake becomes vagueness in scoring.
        </p>

        <form onSubmit={submit} className="space-y-10">
          <div>
            <label className="mono-caps block mb-3">
              Working title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Concierge-run tax prep for freelance creatives"
              className="input-line"
              data-testid="new-idea-title"
              maxLength={140}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
              <label className="mono-caps">
                What is the idea
              </label>
              <div className="flex items-center gap-3">
                <VoiceInput
                  onTranscript={onVoiceTranscript}
                  onInterim={setInterim}
                  size="sm"
                />
                <input
                  ref={fileRef}
                  type="file"
                  accept=".txt,.md,.pdf,.docx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={onFileChosen}
                  className="hidden"
                  data-testid="new-idea-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="btn-ghost inline-flex items-center gap-2"
                  data-testid="new-idea-upload-button"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {uploading ? "Reading…" : "Upload doc"}
                </button>
              </div>
            </div>
            <textarea
              value={description + (interim ? ` ${interim}` : "")}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Problem. Customer. What you're building. Why now. — Or click Upload doc to import a brief (txt / pdf / docx)."
              className="input-line"
              data-testid="new-idea-description"
              rows={10}
            />
            <div className="mt-2 mono-caps text-slate-400 text-[0.65rem]">
              Supported uploads: .txt · .md · .pdf · .docx (max 5MB)
            </div>
          </div>

          <div data-testid="plan-attachment-section">
            <label className="mono-caps block mb-3">
              Attach existing business plan <span className="text-slate-400 normal-case tracking-normal">— optional, PDF up to 25 pages</span>
            </label>
            <p className="text-slate-500 text-sm mb-4 max-w-2xl">
              If you already have a plan written up, attach it and we'll pre-fill
              the Checklist Scorecard from what's in it. The interrogator still
              runs Ring 1; the plan seeds context, it doesn't replace the
              conversation.
            </p>
            <input
              ref={planFileRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => setPlanFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="plan-file-input"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => planFileRef.current?.click()}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="plan-attach-button"
              >
                {planFile ? "Change plan PDF" : "Attach plan PDF"}
              </button>
              {planFile && (
                <span
                  className="mono-caps text-slate-600 text-[0.7rem]"
                  data-testid="plan-attached-filename"
                >
                  {planFile.name} · {(planFile.size / 1024).toFixed(0)} KB
                </span>
              )}
              {planFile && (
                <button
                  type="button"
                  onClick={() => { setPlanFile(null); if (planFileRef.current) planFileRef.current.value = ""; }}
                  className="text-slate-500 hover:text-slate-700 text-xs underline"
                  data-testid="plan-attach-clear"
                >
                  clear
                </button>
              )}
            </div>
          </div>

          {voiceStyles.length > 0 && (
            <div data-testid="voice-style-picker">
              <div className="mono-caps text-slate-500 mb-3">Interrogator voice style</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {voiceStyles.map((s) => {
                  const selected = voiceStyle === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setVoiceStyle(s.id)}
                      className={`text-left rounded-xl border p-4 transition-all ${
                        selected
                          ? "border-slate-900 bg-slate-900 text-white shadow-md"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                      data-testid={`voice-style-${s.id}`}
                      aria-pressed={selected}
                    >
                      <div className={`mono-caps mb-2 text-[0.65rem] ${selected ? "text-slate-300" : "text-slate-500"}`}>
                        {s.label}
                        {selected && <span className="ml-2">✓</span>}
                      </div>
                      <div className={`font-semibold text-sm mb-1.5 ${selected ? "text-white" : "text-slate-900"}`}>
                        {s.tagline}
                      </div>
                      <div className={`text-xs italic ${selected ? "text-slate-300" : "text-slate-500"}`}>
                        {s.tone}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm font-medium" data-testid="new-idea-error">
              {error}
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !title.trim() || !description.trim()}
              data-testid="new-idea-submit"
            >
              {loading ? "Opening Ring 1…" : "Begin Ring 1"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => nav("/dashboard")}
              data-testid="new-idea-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
